import { NEVER_DIRTY, OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { FinancialDetailsHeader } from '@client/components/claims/financials/FinancialDetailsHeader';
import { ICONS } from '@client/constants/icons';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';
import { CreditCard, DollarSign, FileText, User as UserIcon } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Indemnity and expense details',
        },
    },
    params: {
        parse({ compensationId }) {
            return {
                compensationId: BigInt(compensationId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationDetails />
        </PageController>
    );
}

function CompensationDetails() {
    const { compensationId } = Route.useParams();
    const { data } = trpc.compensation.getCompensation.useQuery({
        id: compensationId,
    });
    const navigate = useNavigate();

    return (
        <OverlayPage
            isDirty={NEVER_DIRTY}
            title={<CardTitle icon={<ICONS.payment />} title="Indemnity and expense details" />}
            size="large"
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => <Button onClick={onClose}>Cancel</Button>}
        >
            <FinancialDetailsHeader
                data={{
                    costCode: data?.costCodeFullName,
                    createdAt: data?.createdAt,
                    CreatedBy: {
                        userId: data?.CreatedBy?.userId,
                        photoUrl: data?.CreatedBy?.photoUrl,
                        fullName: data?.CreatedBy?.fullName,
                    },
                }}
                rows={[
                    [
                        {
                            title: 'Amount to pay',
                            type: 'currency',
                            children: data?.amount,
                        },
                    ],
                ]}
            />

            <div className="mb-4 rounded-(--ant-rounded-lg) bg-muted p-4">
                <Typography.Text>{data?.description}</Typography.Text>
            </div>

            <Tabs
                items={[
                    {
                        icon: <DollarSign className="size-4" />,
                        label: 'Financials',
                        to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials',
                    },
                    {
                        icon: <UserIcon className="size-4" />,
                        label: 'Payee info',
                        to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/payee',
                    },
                    {
                        icon: <CreditCard className="size-4" />,
                        label: 'Payment QA',
                        to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/payment',
                    },
                    {
                        icon: <FileText className="size-4" />,
                        label: 'Library',
                        to: '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/library',
                    },
                ]}
            />
        </OverlayPage>
    );
}
